import { useEffect, useRef, useState } from "react";
import Checkbox from "../../../components/Checkbox";
import { Account } from "../../../services/accountService";
import Panel from "../Panel";
import Select from "../../../components/Select";
import useNormalizedBankAccountsQuery from "../../../hooks/data/queries/useNormalizedBankAccountsQuery";
import useCurrentUserAccount from "../../../hooks/data/queries/useCurrentUserAccount";
import Skeleton from "../../../components/Skeleton";
import EditControls from "../EditControls";
import { useSnackbar } from "../../../components/Snackbar";
import useUpdateAccountMutation from "../../../hooks/data/mutations/useUpdateAccountMutation";

type Props = {
  accountHolderCode: string;
  location?: Account;
  migrated: boolean;
  isLoading: boolean;
};

function LocationConfigurations({
  accountHolderCode,
  location,
  migrated,
  isLoading,
}: Props): JSX.Element {
  const primaryAccountCheckboxRef = useRef<HTMLInputElement>(null);

  const [currentLocation, setCurrentLocation] = useState<Account | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const isPrimaryAccount = currentLocation?.primaryAccount ?? false;

  const locationIsModified =
    location?.primaryAccount !== currentLocation?.primaryAccount ||
    location?.bankUUID !== currentLocation?.bankUUID;
  const { data: currentUserAccount } = useCurrentUserAccount();

  const { data: bankAccountsData, isLoading: isLoadingBankAccounts } =
    useNormalizedBankAccountsQuery(accountHolderCode, {
      enabled: Boolean(currentLocation && currentUserAccount?.type === "CSR"),
    });
  const { mutate: updateAccount, isLoading: isUpdatingAccount } =
    useUpdateAccountMutation(accountHolderCode);

  useEffect(() => {
    if (location) {
      setCurrentLocation(location);
    }
  }, [location]);

  useEffect(() => {
    if (isEditing) {
      primaryAccountCheckboxRef.current?.focus();
    }
  }, [isEditing]);

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  const getAdyenBalanceDetailUrl = (accountId: string | undefined) =>
    process.env.REACT_APP_ADYEN_BALANCE_DETAIL_URL_TEMPLATE?.replace(
      "{accountid}",
      accountId ?? ""
    ) ?? "";

  function handleUpdateAccount() {
    if (currentLocation) {
      updateAccount(
        {
          accountHolderId: currentLocation.accountHolderId,
          locationId: currentLocation?.id,
          primaryAccount: currentLocation?.primaryAccount,
          bankUUID: currentLocation?.bankUUID,
          bankAccountLast4: currentLocation.bankAccountLast4,
        },
        {
          onSuccess() {
            openSnackbar(
              `Location ${currentLocation.locationCode} was successfully updated.`,
              {
                type: "success",
              }
            );
            setIsEditing(false);
          },
          onError(error) {
            openSnackbar(
              `Failed to update location ${currentLocation.locationCode}. Please try again.`,
              {
                type: "error",
              }
            );
          },
        }
      );
    }
  }

  return (
    <Panel className="col-span-5">
      <Panel.Header title="Location Configurations">
        <EditControls
          customId={"editLocationButton"}
          isEditing={isEditing}
          isSaving={isUpdatingAccount}
          editButtonProps={{
            variant: "secondary",
            onClick: () => setIsEditing(true),
          }}
          saveButtonProps={{
            onClick: handleUpdateAccount,
            disabled: !locationIsModified,
          }}
          cancelButtonProps={{
            onClick: () => setIsEditing(false),
          }}
        />
      </Panel.Header>
      <Panel.Content>
        <div className="flex flex-col gap-y-3">
          <div className="flex justify-between gap-x-2">
            <div className="font-medium">Location Name</div>
            <div>{isLoading ? <Skeleton /> : location?.locationCode}</div>
          </div>
          <div className="flex justify-between gap-x-2">
            <div className="font-medium">Location Id</div>
            <div className="text-sm">
              <a
                href={getAdyenBalanceDetailUrl(
                  migrated
                    ? location?.balancePlatformAccountId
                    : location?.adyenAccountCode
                )}
                target="adyenSite"
                title="View account in Adyen Console"
                className="text-primary underline"
              >
                {isLoading ? (
                  <Skeleton />
                ) : migrated ? (
                  location?.balancePlatformAccountId
                ) : (
                  location?.adyenAccountCode
                )}
              </a>
            </div>
          </div>
          <div className="flex justify-between gap-x-2">
            <div className="font-medium">Primary Account</div>
            {isLoading ? (
              <Skeleton />
            ) : (
              <Checkbox
                ref={primaryAccountCheckboxRef}
                id="locationPrimaryAccount"
                checked={isPrimaryAccount}
                disabled={!isEditing}
                onChange={() => {
                  if (location) {
                    setCurrentLocation({
                      ...location,
                      primaryAccount: !currentLocation?.primaryAccount,
                    });
                  }
                }}
              />
            )}
          </div>
          <div className="flex justify-between gap-x-2">
            <div className="font-medium">Payout Account</div>
            {isLoading ? (
              <Skeleton />
            ) : (
              <Select
                id="locationBankUUID"
                value={currentLocation?.bankUUID ?? ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const bankAccountDetail = bankAccountsData?.find(
                    (d) => d.id === e.target.selectedOptions[0].value
                  );
                  if (bankAccountDetail && currentLocation) {
                    setCurrentLocation({
                      ...currentLocation,
                      bankUUID: bankAccountDetail.id,
                      bankAccountLast4: bankAccountDetail.last4,
                    });
                  }
                }}
                options={
                  bankAccountsData?.map((detail) => ({
                    value: detail.id,
                    label: detail.label,
                  })) ?? []
                }
                containerProps={{
                  className: "w-1/2",
                }}
                disabled={!isEditing}
                loading={isLoadingBankAccounts}
              />
            )}
          </div>
          <div className="flex justify-between gap-x-2">
            <div className="font-medium">Status</div>
            <div>{isLoading ? <Skeleton /> : location?.status}</div>
          </div>
        </div>
      </Panel.Content>
    </Panel>
  );
}

export default LocationConfigurations;
